import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";

import FilterApi from "@/api/FilterApi";
import { FilterCategory, FilterCollection } from "@/api/generatedTypes";
import { selectedFiltersSelector } from "@/components/filter/selectedFiltersSelector";
import { flatFirstCategoryLevel } from "@/helpers/flatFirstCategoryLevel";

const useSelectedCategory = () => {
  const selectedFilters = useRecoilValue(selectedFiltersSelector);
  const filterQuery = useQuery(
    ["filterQuery", selectedFilters],
    () => FilterApi.filter(selectedFilters),
    {
      keepPreviousData: true,
    }
  );
  const categories = flatFirstCategoryLevel(filterQuery.data?.data.categories);
  const collections = filterQuery.data?.data.collections || [];

  const selectedCategoryId = selectedFilters.categories?.[0];
  const selectedRootCategory = getSelectedRootCategory(
    categories,
    selectedCategoryId
  );
  const selectedCategory = getSelectedCategory(
    selectedRootCategory,
    selectedCategoryId
  );

  const selectedCollectionId = selectedFilters.collections?.[0];
  const selectedRootCollection = getSelectedRootCategory(
    collections,
    selectedCollectionId
  );
  const selectedCollection = getSelectedCategory(
    selectedRootCollection,
    selectedCollectionId
  );

  return {
    selectedRootCategory,
    selectedCategory,
    selectedCategoryId,
    selectedCollectionId,
    selectedRootCollection,
    selectedCollection,
  };
};

const getSelectedRootCategory = (
  categoryTree: FilterCategory[] | FilterCollection[],
  catOrSubCatId?: string
) => {
  if (!catOrSubCatId) return;
  return categoryTree.find((rootCategory) => {
    if (rootCategory.id === catOrSubCatId) {
      return true;
    }
    return rootCategory.children?.find(
      (subCategory) => subCategory.id === catOrSubCatId
    );
  });
};

const getSelectedCategory = (
  rootCategory?: FilterCategory | FilterCollection,
  catOrSubCatId?: string
): FilterCategory | FilterCollection | undefined => {
  if (!rootCategory) return;
  if (rootCategory.id === catOrSubCatId) return rootCategory;
  return rootCategory.children?.find((subCat) => subCat.id === catOrSubCatId);
};

export default useSelectedCategory;
